// ---------------------------------------------------------------------------
// TemplateEditor — Create / Edit template page (UI-02)
// ---------------------------------------------------------------------------

import { useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { ArrowLeft, Eye, Play } from 'lucide-react';
import { useTemplate, useCreateTemplate, useUpdateTemplate } from '@/hooks/use-templates';
import { TemplateForm } from '@/components/template/TemplateForm';
import { PromptTree } from '@/components/template/PromptTree';
import { DataGroupPanel } from '@/components/template/DataGroupPanel';
import { AudioPanel } from '@/components/audio/AudioPanel';
import { SurveyPlayer } from '@/components/template/SurveyPlayer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { TooltipProvider } from '@/components/ui/tooltip';
import type { TemplatePrompt } from '@/types/template';

interface TemplateEditorProps {
  mode: 'create' | 'edit';
}

export default function TemplateEditor({ mode }: TemplateEditorProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: template, isLoading } = useTemplate(mode === 'edit' ? id : undefined);
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate(id ?? '');

  const [audioPrompt, setAudioPrompt] = useState<TemplatePrompt | null>(null);
  const [playerOpen, setPlayerOpen] = useState(false);
  const saveRef = useRef<HTMLDivElement>(null);
  const scrollToSave = useCallback(() => {
    saveRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  async function handleFormSubmit(values: {
    title: string;
    type: string;
    customerId: number;
    diseaseAssociation?: string;
    cadenceUnit?: 'day' | 'week' | 'month' | '' | undefined;
    cadenceCount?: number | '' | undefined;
    cadenceAnchor?:
      | 'enrollment'
      | 'case_created'
      | 'last_completion'
      | ''
      | undefined;
  }) {
    // Normalize the cadence triple. The zod refine on TemplateForm already
    // enforced "all-or-nothing", so here we either pass all three or omit them.
    const cadenceConfigured =
      values.cadenceUnit && values.cadenceCount && values.cadenceAnchor;
    const cadencePayload = cadenceConfigured
      ? {
          cadenceUnit: values.cadenceUnit as 'day' | 'week' | 'month',
          cadenceCount: Number(values.cadenceCount),
          cadenceAnchor: values.cadenceAnchor as
            | 'enrollment'
            | 'case_created'
            | 'last_completion',
        }
      : undefined;

    try {
      if (mode === 'create') {
        const result = await createTemplate.mutateAsync({
          title: values.title,
          type: values.type as 'scheduled_education' | 'biometric_triggered' | 'adherence' | 'patient_satisfaction',
          customerId: values.customerId,
          diseaseAssociation: values.diseaseAssociation || undefined,
          ...cadencePayload,
        });
        toast({ title: 'Template created', variant: 'success' });
        navigate(`/templates/${result.template_id}`);
      } else {
        await updateTemplate.mutateAsync({
          title: values.title,
          customerId: values.customerId,
          diseaseAssociation: values.diseaseAssociation || undefined,
          // On edit: pass cadence if configured, OR clear all three with null
          // when the user blanked the section (zod refine guarantees we never
          // receive a partial triple).
          ...(cadencePayload
            ? cadencePayload
            : {
                cadenceUnit: null,
                cadenceCount: null,
                cadenceAnchor: null,
              }),
        });
        toast({ title: 'Template saved', variant: 'success' });
      }
    } catch {
      toast({ title: 'Failed to save template', variant: 'destructive' });
    }
  }

  if (mode === 'edit' && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const isBiometric = template?.type === 'biometric_triggered';

  return (
    <TooltipProvider>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/templates">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h2 className="text-xl font-semibold text-carbon-900">
                {mode === 'create' ? 'Create Template' : template?.title ?? 'Edit Template'}
              </h2>
              <p className="text-sm text-carbon-500">
                {mode === 'create'
                  ? 'Configure a new survey template'
                  : `Template ${id}`}
              </p>
            </div>
          </div>
          {mode === 'edit' && id && (
            <div className="flex items-center gap-2">
              {template && template.sessions.length > 0 && (
                <Button variant="secondary" onClick={() => setPlayerOpen(true)}>
                  <Play className="h-4 w-4 mr-1" /> Preview
                </Button>
              )}
              <Button variant="secondary" asChild>
                <Link to={`/templates/${id}/review`}>
                  <Eye className="h-4 w-4 mr-1" /> Review
                </Link>
              </Button>
            </div>
          )}
        </div>

        {/* Template metadata form */}
        <div className="rounded-ds-008 border border-silver-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-carbon-700 mb-3">Template Details</h3>
          <TemplateForm
            template={template}
            mode={mode}
            onSubmit={handleFormSubmit}
            isPending={createTemplate.isPending || updateTemplate.isPending}
            hideSubmit={mode === 'edit'}
          />
        </div>

        {/* Prompt tree + data groups (edit mode only) */}
        {mode === 'edit' && template && id && (
          <>
            <Tabs defaultValue="prompts">
              <TabsList>
                <TabsTrigger value="prompts">Prompts</TabsTrigger>
                {isBiometric && (
                  <TabsTrigger value="data-groups">Data Groups</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="prompts">
                <PromptTree
                  templateId={id}
                  sessions={template.sessions}
                  onAudioOpen={setAudioPrompt}
                  onScrollAfterAdd={scrollToSave}
                />
              </TabsContent>

              {isBiometric && (
                <TabsContent value="data-groups">
                  <DataGroupPanel templateId={id} />
                </TabsContent>
              )}
            </Tabs>

            <div ref={saveRef} className="flex justify-center">
              <Button
                type="submit"
                form="template-form"
                disabled={createTemplate.isPending || updateTemplate.isPending}
              >
                {updateTemplate.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </>
        )}

        {/* Audio side panel */}
        {audioPrompt && audioPrompt.question_id && (
          <AudioPanel
            questionId={audioPrompt.question_id}
            open={!!audioPrompt}
            onOpenChange={(open) => { if (!open) setAudioPrompt(null); }}
          />
        )}

        {/* Survey player / preview */}
        {template && (
          <SurveyPlayer
            open={playerOpen}
            onOpenChange={setPlayerOpen}
            sessions={template.sessions}
            templateTitle={template.title}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
